/* Fuel/MXN linear correlation — Fase B of the Forecast v2 roadmap
   ("Fuel/MXN correcciones lineales... Todo esto sin AI, reglas puras").
   No UI, same convention as fobPricesForecast.js / holidayImpactCalc.js.

   Computes a real, honest linear relationship (ordinary least squares +
   Pearson correlation) between a macro series (diesel $/gal or MXN/USD)
   and a berry price series, both aggregated to the same ISO weeks, over
   whatever overlapping history both have (macro_indicators: 7 years,
   2019-2026; historical_prices/USDA: same window per the 2026-09-15
   backfill). A pure statistical correction, not a model prediction —
   the caller decides what to do with slope/correlation (e.g. show it as
   context, or nudge a forecast by slope × how far the LATEST macro
   value sits from its own recent average). */

function getISOWeek(dateStr) {
  var d = new Date(dateStr + "T00:00:00");
  var target = new Date(d.valueOf());
  var dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  var firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  return 1 + Math.ceil((firstThursday - target) / 604800000);
}
function weekKey(dateStr) { var d = new Date(dateStr + "T00:00:00"); return d.getFullYear() + "|W" + getISOWeek(dateStr); }
function average(nums) { return nums.length ? nums.reduce(function (s, v) { return s + v; }, 0) / nums.length : null; }
function round3(n) { return Math.round(n * 1000) / 1000; }

/* macroRows: app-shape macro_indicators rows (metric/value/reportDate),
   already filtered to ONE metric by the caller. Returns { weekKey: avgValue }. */
function weeklyAvgByMetric(macroRows) {
  var buckets = {};
  (macroRows || []).forEach(function (r) {
    if (!r || !r.reportDate || r.value == null) return;
    var k = weekKey(r.reportDate);
    if (!buckets[k]) buckets[k] = [];
    buckets[k].push(parseFloat(r.value));
  });
  var out = {};
  Object.keys(buckets).forEach(function (k) { out[k] = average(buckets[k]); });
  return out;
}

/* priceRows: app-shape historical_prices rows, already filtered to ONE
   source + product by the caller. Returns { weekKey: avgMidPrice }. */
function weeklyAvgPrice(priceRows) {
  var buckets = {};
  (priceRows || []).forEach(function (r) {
    if (!r || !r.reportDate) return;
    var mid = (r.priceLow != null && r.priceHigh != null) ? (parseFloat(r.priceLow) + parseFloat(r.priceHigh)) / 2 : null;
    if (mid == null || isNaN(mid)) return;
    var k = weekKey(r.reportDate);
    if (!buckets[k]) buckets[k] = [];
    buckets[k].push(mid);
  });
  var out = {};
  Object.keys(buckets).forEach(function (k) { out[k] = average(buckets[k]); });
  return out;
}

/* Ordinary least squares (y = slope*x + intercept) + Pearson r, over
   whatever weeks have BOTH a macro value and a price value. Returns
   null if fewer than MIN_POINTS weeks overlap (not enough to say
   anything statistically honest). */
export function computeLinearCorrection(macroRows, priceRows) {
  var MIN_POINTS = 20;
  var macroByWeek = weeklyAvgByMetric(macroRows);
  var priceByWeek = weeklyAvgPrice(priceRows);

  var xs = [], ys = [];
  Object.keys(macroByWeek).forEach(function (k) {
    if (priceByWeek[k] == null) return;
    xs.push(macroByWeek[k]);
    ys.push(priceByWeek[k]);
  });
  var n = xs.length;
  if (n < MIN_POINTS) return { pointsUsed: n, enough: false, slope: null, intercept: null, correlation: null, latestMacro: null, macroBaseline: null };

  var xMean = average(xs), yMean = average(ys);
  var covXY = 0, varX = 0, varY = 0;
  for (var i = 0; i < n; i++) {
    var dx = xs[i] - xMean, dy = ys[i] - yMean;
    covXY += dx * dy; varX += dx * dx; varY += dy * dy;
  }
  var slope = varX !== 0 ? covXY / varX : 0;
  var intercept = yMean - slope * xMean;
  var correlation = (varX > 0 && varY > 0) ? covXY / Math.sqrt(varX * varY) : 0;

  /* "Latest vs baseline" context — latest = most recent 4-week average
     of the macro series, baseline = the full-history average — so the
     caller can say "diesel is currently $X above its historical
     average, which historically nudges price by slope*X". */
  var sortedWeeks = Object.keys(macroByWeek).sort();
  var recentWeeks = sortedWeeks.slice(-4);
  var latestMacro = average(recentWeeks.map(function (k) { return macroByWeek[k]; }).filter(function (v) { return v != null; }));

  return {
    pointsUsed: n,
    enough: true,
    slope: round3(slope),
    intercept: round3(intercept),
    correlation: round3(correlation),
    latestMacro: latestMacro != null ? round3(latestMacro) : null,
    macroBaseline: round3(xMean),
  };
}
